import { API } from '../api.js';
import { createContext, useContext, useState, useEffect, useCallback } from 'react';

function pingHeartbeat(token) {
  if (!token) return;
  fetch(`${API}/api/heartbeat`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
  }).catch(() => {});
}

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser]       = useState(null);
  const [token, setToken]     = useState(() => localStorage.getItem('lh_token'));
  const [loading, setLoading] = useState(true);

  // Restore session on mount — /api/auth/me also stamps last_seen_at server-side
  useEffect(() => {
    if (!token) { setLoading(false); return; }
    fetch(`${API}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(u => { if (u) setUser(u); else clearAuth(); })
      .catch(() => clearAuth())
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  // Heartbeat: keep last_seen_at fresh every 60 s
  useEffect(() => {
    if (!token) return;
    pingHeartbeat(token);                          // fire immediately on token available
    const id = setInterval(() => pingHeartbeat(token), 60_000);
    return () => clearInterval(id);
  }, [token]);

  function clearAuth() {
    localStorage.removeItem('lh_token');
    setToken(null);
    setUser(null);
  }

  const login = useCallback(async (email, password) => {
    const res  = await fetch(`${API}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Login failed');
    localStorage.setItem('lh_token', data.token);
    setToken(data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const signup = useCallback(async (email, password, display_name) => {
    const res  = await fetch(`${API}/api/auth/signup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, display_name }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Signup failed');
    localStorage.setItem('lh_token', data.token);
    setToken(data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const loginWithProvider = useCallback((provider) => {
    return new Promise((resolve, reject) => {
      const width = 500;
      const height = 650;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      
      const popup = window.open(
        `${API}/api/auth/${provider}/login`,
        'oauth-popup',
        `width=${width},height=${height},left=${left},top=${top},status=no,resizable=yes`
      );

      if (!popup) {
        reject(new Error('Popup blocked! Please enable popups for this site.'));
        return;
      }

      const handleMessage = (event) => {
        // Accept messages from backend API
        if (event.origin !== API) return;
        
        if (event.data?.type === 'AUTH_SUCCESS') {
          const { token: jwtToken, user: userData } = event.data;
          localStorage.setItem('lh_token', jwtToken);
          setToken(jwtToken);
          setUser(userData);
          window.removeEventListener('message', handleMessage);
          resolve(userData);
        } else if (event.data?.type === 'AUTH_ERROR') {
          window.removeEventListener('message', handleMessage);
          reject(new Error(event.data.error || 'Authentication failed'));
        }
      };

      window.addEventListener('message', handleMessage);

      // Check if popup closed by user
      const checkClosed = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkClosed);
          window.removeEventListener('message', handleMessage);
          // Let a small delay pass in case message handler resolved it first
          setTimeout(() => {
            reject(new Error('Login window closed.'));
          }, 100);
        }
      }, 1000);
    });
  }, []);

  const logout = useCallback(() => clearAuth(), []);

  const refreshUser = useCallback(async () => {
    if (!token) return;
    const res  = await fetch(`${API}/api/users/me`, { headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) { const u = await res.json(); setUser(u); }
  }, [token]);

  const authFetch = useCallback((url, opts = {}) => {
    return fetch(`${API}${url}`, {
      ...opts,
      headers: { ...opts.headers, Authorization: `Bearer ${token}` },
    });
  }, [token]);

  const isGuest = !user;
  const isUser = user && user.role !== 'admin';
  const isAdmin = user?.role === 'admin';

  return (
    <AuthContext.Provider value={{ user, token, loading, login, signup, loginWithProvider, logout, refreshUser, authFetch, API, isGuest, isUser, isAdmin }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() { return useContext(AuthContext); }
